import { store } from '@/app/store';
import { logger } from '@/shared/utils';
import { UPLOAD_STATUS } from '@/shared/constants';
import { updateInviteStatus } from '@/features/invite/model/inviteSlice';
import { selectInvitePermissions } from '@/features/invite/model/selectors';
import { formatUserIdToJid } from '../utils/jidIdFormat';
import { updateMediaUploadStatus } from './queueUtils';
import { dispatchTextMessage, dispatchMeetMessage, dispatchEditMessage } from './messageDispatchers';
import { dispatchMediaMessage, cancelUpload as sdkCancelUpload } from './mediaUploadHandler';

// ─── Queue State ──────────────────────────────────────────────────────────────

const uploadQueues = new Map();
const activeChats = new Set();

// ─── Queue Helpers ────────────────────────────────────────────────────────────

const getQueue = chatId => {
   if (!uploadQueues.has(chatId)) uploadQueues.set(chatId, []);
   return uploadQueues.get(chatId);
};

const isQueued = (queue, msgId) => queue.some(({ messageObject }) => messageObject.msgId === msgId);

// ─── Queue Processor ──────────────────────────────────────────────────────────

async function drainQueue(chatId) {
   const queue = getQueue(chatId);
   if (activeChats.has(chatId) || queue.length === 0) {
      return;
   }

   activeChats.add(chatId);
   const currentItem = queue[0];
   const { messageObject, uploadOptions, messageType, isResume } = currentItem;
   const { userJid, msgId } = messageObject;
   const recipientJid = userJid ?? formatUserIdToJid(chatId);

   try {
      if (messageObject.editMessageId !== messageObject.msgId) {
         await dispatchEditMessage(messageObject);
      } else if (messageType === 'text') {
         await dispatchTextMessage(messageObject, recipientJid);
      } else if (messageType === 'meet') {
         await dispatchMeetMessage(messageObject);
      } else {
         await dispatchMediaMessage(messageObject, chatId, recipientJid, uploadOptions, isResume, messageType);
      }
   } catch (err) {
      logger.error('[MessageQueue] Process error:', err);
      if (messageType !== 'text') {
         updateMediaUploadStatus({ chatId, msgId, is_uploading: UPLOAD_STATUS.FAILED });
      }
   } finally {
      const idx = queue.indexOf(currentItem);
      if (idx !== -1) queue.splice(idx, 1);

      activeChats.delete(chatId);
      drainQueue(chatId);
   }
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function queueMessage(messageObject, chatId, messageType, uploadOptions = {}, isResume = false) {
   const queue = getQueue(chatId);
   const { msgId } = messageObject;

   if (isResume) {
      const staleIndex = queue.findIndex(item => item.messageObject.msgId === msgId);
      if (staleIndex !== -1) queue.splice(staleIndex, 1);
      queue.push({ messageObject, uploadOptions, messageType, isResume });
   } else if (!isQueued(queue, msgId)) {
      queue.push({ messageObject, uploadOptions, messageType });
   }

   if (messageType !== 'text' && messageType !== 'meet') {
      updateMediaUploadStatus({ chatId, msgId, is_uploading: UPLOAD_STATUS.QUEUED });
   }

   // Returned so callers that need to send to several chatIds one at a time (e.g.
   // scheduling a meeting to multiple recipients) can await real dispatch completion
   // instead of guessing a fixed delay between recipients.
   return drainQueue(chatId);
}

export const queueFile = (messageObject, chatId, fileOptions, messageType, isResume = false) => {
   queueMessage(messageObject, chatId, messageType, { fileOptions }, isResume);
};

// Optimistically reduce the sender's pending-invite message quota by the number of
// messages actually being sent in this batch. Text used to drop the quota via a
// post-send server sync; media never did (its success response has a different shape),
// so the limit was never reached and sends slipped past it silently. Reducing here —
// once per drained batch, by the messages array length — keeps text and media
// consistent and immediate. Edits (editMessageId !== msgId) don't consume quota.
const reduceInviteQuotaOnSend = (chatId, recipientJid, messagesArray) => {
   const sentCount = messagesArray.filter(m => m.editMessageId === m.msgId).length;
   if (sentCount <= 0) return;
   const { inviteStatus, isReceiver, remainingMessages } = selectInvitePermissions(store.getState(), recipientJid);
   if (inviteStatus !== 1 || isReceiver) return;
   store.dispatch(
      updateInviteStatus({ userId: chatId, remainingMessages: Math.max(0, remainingMessages - sentCount) }),
   );
};

export const queueMessages = (messagesArray, recipientJid) => {
   const chatId = recipientJid.split('@')[0];
   const dispatched = Promise.all(
      messagesArray.map(({ msgType, ...data }) => queueMessage({ msgType, ...data }, chatId, msgType)),
   );
   reduceInviteQuotaOnSend(chatId, recipientJid, messagesArray);
   return dispatched;
};

export const resumeUpload = (messageObject, chatId, messageType) => {
   // Re-queue as a fresh entry with isResume:true so the SDK resumes progress
   // from where it left off rather than restarting from 0%.
   queueMessage(messageObject, chatId, messageType, {}, true);
};

export const processAllQueues = () => {
   for (const chatId of uploadQueues.keys()) {
      drainQueue(chatId);
   }
};

/**
 * Remove a message from the queue by msgId.
 * Called when a cancel is issued so queued items behind it are unblocked.
 */
export const removeFromQueue = (msgId, chatId) => {
   const queue = getQueue(chatId);
   const idx = queue.findIndex(item => item.messageObject.msgId === msgId);
   if (idx !== -1) queue.splice(idx, 1);
};

/**
 * Cancel an in-progress or queued upload:
 *   1. Remove from queue  → unblocks any messages queued behind it.
 *   2. Call SDK cancel    → stops the active network upload.
 *   3. Mark status FAILED → UI shows the retry button.
 */
export const cancelUpload = async (msgId, chatId) => {
   removeFromQueue(msgId, chatId);
   await sdkCancelUpload(msgId, chatId);
};
